/**
 * Cores para participantes (speakers) de uma transcrição.
 *
 * Estratégia (versão atual): paleta PRÉ-DEFINIDA, curada manualmente para
 * garantir alto contraste entre cores adjacentes — tornando trivial para
 * o usuário identificar o dono da fala apenas pela cor, sem confusão
 * entre pares de cores próximas no espectro.
 *
 * Anteriormente usávamos geração algorítmica via OKLCh (farthest-point
 * sampling no círculo cromático). Embora matematicamente correto, na
 * prática o resultado misturava tons quentes próximos (laranja/vermelho)
 * o suficiente para confundir os revisores. A paleta abaixo foi
 * escolhida com base em paletas categoricamente otimizadas (referência
 * geral: Tableau 10 e ColorBrewer Set1) e ordenada para que os primeiros
 * slots sejam SEMPRE os que têm maior contraste mútuo:
 *
 *   slot 0 → azul,    slot 1 → vermelho, slot 2 → verde,
 *   slot 3 → laranja, slot 4 → roxo,     slot 5 → ciano-escuro/teal,
 *   slot 6 → marrom,  slot 7 → rosa,     slot 8 → cinza-escuro,
 *   slot 9 → oliva.
 *
 * Cada tema (claro/escuro) tem sua própria versão das cores principais
 * para preservar legibilidade contra o fundo, mas a ORDEM dos slots é a
 * mesma — o speaker no slot N vê "a mesma cor conceitual" em ambos os
 * temas.
 *
 * Por que paleta fixa em vez de gerar?
 *   - Reconhecimento rápido por cor: usuários veem um conjunto SEMPRE
 *     consistente entre transcrições (azul = primeiro speaker, etc.).
 *   - Sem cálculos por render: zero matemática de cor no caminho quente
 *     da renderização (cada parágrafo da transcrição renderiza um badge).
 *   - Acessibilidade: cores escolhidas para terem contraste contra o
 *     fundo de cada tema, e tons "afastados" entre vizinhos.
 */

/**
 * Tamanho fixo da paleta. Se a transcrição tiver mais speakers que isso,
 * ciclamos do início (com aviso visual: ainda funcional, mas pode haver
 * matizes repetidos). Mantemos em 10 — número típico em entrevistas e
 * suficiente para a maioria dos casos.
 */
export const MAX_SPEAKERS = 10;

/* ─────────────── Paletas pré-definidas ─────────────── */

/**
 * Paletas para tema CLARO (fundo branco/cinza muito claro).
 * Cores escolhidas com luminosidade média (~0.45-0.55) para boa
 * legibilidade quando aplicadas em texto e como fundo sutil.
 *
 * Cada slot define:
 *   - `color`   : cor principal (texto, ícone, borda forte)
 *   - `onColor` : cor de texto sobre fundo `color` (preto ou branco)
 *   - `surface` : fundo sutil para destacar a área do speaker (rgba)
 *   - `border`  : borda de média intensidade (rgba)
 */
const PALETTE_LIGHT = [
  { color: "#1976d2", onColor: "#ffffff", surface: "rgba(25, 118, 210, 0.12)",  border: "rgba(25, 118, 210, 0.45)"  }, // azul
  { color: "#d32f2f", onColor: "#ffffff", surface: "rgba(211, 47, 47, 0.12)",   border: "rgba(211, 47, 47, 0.45)"   }, // vermelho
  { color: "#388e3c", onColor: "#ffffff", surface: "rgba(56, 142, 60, 0.12)",   border: "rgba(56, 142, 60, 0.45)"   }, // verde
  { color: "#f57c00", onColor: "#1e1e1e", surface: "rgba(245, 124, 0, 0.14)",   border: "rgba(245, 124, 0, 0.50)"   }, // laranja
  { color: "#7b1fa2", onColor: "#ffffff", surface: "rgba(123, 31, 162, 0.12)",  border: "rgba(123, 31, 162, 0.45)"  }, // roxo
  { color: "#00838f", onColor: "#ffffff", surface: "rgba(0, 131, 143, 0.12)",   border: "rgba(0, 131, 143, 0.45)"   }, // teal
  { color: "#5d4037", onColor: "#ffffff", surface: "rgba(93, 64, 55, 0.12)",    border: "rgba(93, 64, 55, 0.45)"    }, // marrom
  { color: "#c2185b", onColor: "#ffffff", surface: "rgba(194, 24, 91, 0.12)",   border: "rgba(194, 24, 91, 0.45)"   }, // rosa
  { color: "#455a64", onColor: "#ffffff", surface: "rgba(69, 90, 100, 0.12)",   border: "rgba(69, 90, 100, 0.45)"   }, // cinza-azulado
  { color: "#827717", onColor: "#ffffff", surface: "rgba(130, 119, 23, 0.14)",  border: "rgba(130, 119, 23, 0.50)"  }, // oliva
];

/**
 * Paletas para tema ESCURO (fundo azul-marinho profundo).
 * Cores deslocadas para tons mais claros (~0.65-0.78 de luminosidade) para
 * manter legibilidade contra o fundo escuro.
 *
 * Mantém a MESMA ordem semântica do `PALETTE_LIGHT`: slot 0 é "azul" em
 * ambos os temas, mesmo que o tom específico seja diferente.
 */
const PALETTE_DARK = [
  { color: "#64b5f6", onColor: "#0a1929", surface: "rgba(100, 181, 246, 0.18)", border: "rgba(100, 181, 246, 0.55)" }, // azul-claro
  { color: "#ef5350", onColor: "#1a0707", surface: "rgba(239, 83, 80, 0.18)",   border: "rgba(239, 83, 80, 0.55)"   }, // vermelho-claro
  { color: "#81c784", onColor: "#0a1d0e", surface: "rgba(129, 199, 132, 0.18)", border: "rgba(129, 199, 132, 0.55)" }, // verde-claro
  { color: "#ffb74d", onColor: "#1f1402", surface: "rgba(255, 183, 77, 0.20)",  border: "rgba(255, 183, 77, 0.55)"  }, // laranja-claro
  { color: "#ba68c8", onColor: "#1a0a1f", surface: "rgba(186, 104, 200, 0.18)", border: "rgba(186, 104, 200, 0.55)" }, // roxo-claro
  { color: "#4dd0e1", onColor: "#0a1d20", surface: "rgba(77, 208, 225, 0.18)",  border: "rgba(77, 208, 225, 0.55)"  }, // ciano-claro
  { color: "#a1887f", onColor: "#19120e", surface: "rgba(161, 136, 127, 0.18)", border: "rgba(161, 136, 127, 0.55)" }, // marrom-claro
  { color: "#f06292", onColor: "#1f0a13", surface: "rgba(240, 98, 146, 0.18)",  border: "rgba(240, 98, 146, 0.55)"  }, // rosa-claro
  { color: "#b0bec5", onColor: "#0e1417", surface: "rgba(176, 190, 197, 0.18)", border: "rgba(176, 190, 197, 0.55)" }, // cinza-claro
  { color: "#dce775", onColor: "#181a06", surface: "rgba(220, 231, 117, 0.18)", border: "rgba(220, 231, 117, 0.55)" }, // oliva-claro
];

/* ─────────────── API pública ─────────────── */

/**
 * Devolve a paleta completa do tema. Sempre retorna `MAX_SPEAKERS` slots.
 *
 * @param {"light"|"dark"} themeMode
 * @returns {{color:string,onColor:string,surface:string,border:string}[]}
 */
export function buildSpeakerPalette(themeMode) {
  return themeMode === "dark" ? PALETTE_DARK : PALETTE_LIGHT;
}

/**
 * Resolve, dado um speaker_id e uma lista ordenada de speakers, qual é o
 * índice (slot) que esse speaker ocupa. Se o speaker_id não estiver
 * presente, devolve -1. O índice é estável: depende apenas da ordem em
 * que o speaker aparece em `speakers`.
 *
 * @param {string} speakerId
 * @param {{speaker_id: string}[]} speakers
 * @returns {number}
 */
export function speakerSlotIndex(speakerId, speakers) {
  if (!Array.isArray(speakers) || !speakerId) return -1;
  return speakers.findIndex((s) => s?.speaker_id === speakerId);
}

/**
 * Retorna a paleta (color/onColor/surface/border) para um dado speaker,
 * dado o tema ativo e a lista de speakers da transcrição. Se houver mais
 * speakers que `MAX_SPEAKERS`, ciclamos a paleta usando `idx % MAX_SPEAKERS`
 * — ainda funcional, mas com possível repetição de cor.
 *
 * @param {string} speakerId
 * @param {{speaker_id:string}[]} speakers
 * @param {"light"|"dark"} themeMode
 * @returns {{color:string,onColor:string,surface:string,border:string}}
 */
export function colorForSpeaker(speakerId, speakers, themeMode) {
  const palette = buildSpeakerPalette(themeMode);
  const idx = speakerSlotIndex(speakerId, speakers);
  if (idx >= 0) {
    return palette[idx % palette.length];
  }
  /* Speaker desconhecido: cor neutra (cinza) para não "queimar" um slot
     da paleta com algo temporário. */
  return themeMode === "dark"
    ? {
        color: "#a0a0b0",
        onColor: "#1e1e2e",
        surface: "rgba(160,160,176,0.18)",
        border: "rgba(160,160,176,0.45)",
      }
    : {
        color: "#666666",
        onColor: "#ffffff",
        surface: "rgba(0,0,0,0.06)",
        border: "rgba(0,0,0,0.18)",
      };
}
